import { useEffect, useMemo } from 'react';

import { gql, makeVar } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import type { Core } from '@pdftron/webviewer';
import { getViewerDocInfo } from 'src/components/viewer/FileViewerBody';
import type { EditorTabStateActions } from 'src/editor/editor-tab-state-registry';
import { getEditorTabStateActionsForTabId } from 'src/editor/editor-tab-state-registry';
import { Apollo } from 'src/gql/generated';
import { staticFileVersionTypename } from 'src/helpers';
import { useDebouncedCallback } from 'use-debounce';
import { v4 as uuid4 } from 'uuid';

export const syncingAnnotationsVar = makeVar<boolean>(false);

type AnnotationSyncViewerActions = Pick<
  EditorTabStateActions['viewer'],
  'incrementViewerAnnotationsChangedCount' | 'setSyncingDoc'
>;

const noopAnnotationSyncViewerActions: AnnotationSyncViewerActions = {
  incrementViewerAnnotationsChangedCount: () => undefined,
  setSyncingDoc: () => undefined,
};

export function useSyncAnnotations(tabId: UUID | null) {
  const client = useApolloClient();
  const {
    viewer: { incrementViewerAnnotationsChangedCount, setSyncingDoc },
  } = useMemo(() => {
    if (tabId) return getEditorTabStateActionsForTabId(tabId);
    return { viewer: noopAnnotationSyncViewerActions };
  }, [tabId]);
  const [upsertAnnotations, { loading: syncingAnnotations }] =
    Apollo.useUpsertAnnotationsMutation();
  const syncAnnotations = async (
    documentViewer: Core.DocumentViewer,
    options: { mergeXFDFIntoFile?: boolean } = {}
  ) => {
    const { mergeXFDFIntoFile = false } = options;
    const annotationManager = documentViewer.getAnnotationManager();
    const { fileVersionId, isSigned } = getViewerDocInfo(documentViewer);
    if (isSigned) return {};

    setSyncingDoc(true);
    const xfdf = await annotationManager.exportAnnotations({
      fields: true,
      links: false,
      widgets: true,
    });
    const currentFileVersion = client.readFragment<{
      __typename: 'StaticFileVersion';
      id: string;
      annotationsXFDF?: { __typename: 'AnnotationsXFDF'; id: string } | null;
    }>({
      fragment: gql`
        fragment CurrentFileVersion on StaticFileVersion {
          __typename
          id
          annotationsXFDF {
            __typename
            id
          }
        }
      `,
      id: client.cache.identify({
        __typename: staticFileVersionTypename,
        id: fileVersionId,
      }),
    });

    // Important to update optimistically here so that the annotations subscription processes XFDF from remote peers correctly.
    const res = await upsertAnnotations({
      optimisticResponse: {
        __typename: 'Mutation',
        upsertAnnotations: {
          __typename: 'AnnotationsXFDF',
          id: currentFileVersion?.annotationsXFDF?.id ?? uuid4(),
          xfdf,
        },
      },
      variables: {
        input: {
          mergeXFDFIntoFile,
          props: {
            fileVersionId,
            xfdf,
          },
        },
      },
    });
    setSyncingDoc(false);
    incrementViewerAnnotationsChangedCount();

    return res;
  };
  const debouncedSyncAnnotations = useDebouncedCallback(syncAnnotations, 1000);

  useEffect(() => {
    syncingAnnotationsVar(syncingAnnotations);
  }, [syncingAnnotations]);

  return { debouncedSyncAnnotations, syncAnnotations };
}
